import { MortgageProps } from '../contexts/MortgageContextProvider';
import { useEffect, useState } from 'react';

type ComparisonsProps = {
    showOffset: boolean,
    showTwoMortgages: boolean,
    showOverpayment: boolean,
    mortgageData: MortgageProps,
    secondMortgageData: MortgageProps,
    offsetData: MortgageProps,
    overpaymentData: MortgageProps
}

const formatCurrency = (amount: number) => {
    const pounds = Math.floor(amount);
    const pennies = Math.round((amount - pounds) * 100);
    return { pounds, pennies: pennies < 10 ? `0${pennies}` : pennies };
};

const OffsetComparison = ({ saving, timeSaved }: { saving: number, timeSaved: number }) => {
    const { pounds: savingPounds, pennies: savingPennies } = formatCurrency(saving ? Math.round(saving * 100) / 100 : 0);

    return (
        <>
            <div className="flex flex-col items-start pb-4">
                <p className="text-sm font-medium">Interest saved from Offset</p>
                <p className="text-3xl font-bold"><span>£{savingPounds}<span className="text-sm">.{savingPennies}</span></span></p>
            </div>
            <div className="flex flex-col items-start pb-4">
                <p className="text-sm font-medium">Time saved from Offset</p>
                <p className="text-3xl font-bold">{Math.floor(timeSaved / 12)} years</p>
                <p className="text-sm font-medium">{timeSaved % 12} months</p>
            </div>
        </>
    );
};

const OverpaymentComparison = ({ savingInterest, timeSaved, totalSaved }: { savingInterest: number, timeSaved: number, totalSaved: number }) => {
    const { pounds: savingInterestPounds, pennies: savingInterestPennies } = formatCurrency(savingInterest ? Math.round(savingInterest * 100) / 100 : 0);
    const { pounds: totalSavedPounds, pennies: totalSavedPennies } = formatCurrency(totalSaved ? Math.round(totalSaved * 100) / 100 : 0);

    return (
        <>
            {/* <div className="flex flex-col items-start pb-4">
                <p className="text-sm font-medium">Interest saved from Overpayment</p>
                <p className="text-3xl font-bold"><span>£{savingInterestPounds}<span className="text-sm">.{savingInterestPennies}</span></span></p>
            </div> */}
            <div className="flex flex-col items-start pb-4">
                <p className="text-sm font-medium">Total saved from Overpayment</p>
                <p className="text-3xl font-bold"><span>£{totalSavedPounds}<span className="text-sm">.{totalSavedPennies}</span></span></p>
            </div>
            <div className="flex flex-col items-start pb-4">
                <p className="text-sm font-medium">Time saved from Overpayment</p>
                <p className="text-3xl font-bold">{Math.floor(timeSaved / 12)} years</p>
                <p className="text-sm font-medium">{timeSaved % 12} months</p>
            </div>
        </>
    );
};

const MortgageComparison = ({ saving, cheaperMortgage, interestSaved, cheaperInterest }: { saving: number, cheaperMortgage: string, interestSaved: number, cheaperInterest: string }) => {
    const { pounds: savingPounds, pennies: savingPennies } = formatCurrency(saving ? Math.round(saving * 100) / 100 : 0);
    const { pounds: interestSavedPounds, pennies: interestSavedPennies } = formatCurrency(interestSaved ? Math.round(interestSaved * 100) / 100 : 0);

    return (
        <>
            <div className="flex flex-col items-start pb-4">
                <p className="text-l font-medium">{cheaperMortgage} will save you</p>
                <p className="text-3xl font-bold"><span>£{savingPounds}<span className="text-sm">.{savingPennies}</span></span></p>
            </div>
            <div className="flex flex-col items-start pb-4">
                <p className="text-l font-medium">{cheaperInterest} has</p>
                <p className="text-3xl font-bold"><span>£{interestSavedPounds}<span className="text-sm">.{interestSavedPennies}</span></span></p>
                <p className="text-sm font-medium">less interest</p>
            </div>
        </>
    );
};


const Comparisons = ({ showOffset, showOverpayment, showTwoMortgages, mortgageData, secondMortgageData, offsetData, overpaymentData }: ComparisonsProps) => {
    const [offsetSavingsInterest, setOffsetSavingsInterest] = useState<number>(0); // same as total saved
    const [offsetTimeSaved, setOffsetTimeSaved] = useState<number>(0);
    const [overpaymentSavingsInterest, setOverpaymentSavingsInterest] = useState<number>(0);
    const [overpaymentTimeSaved, setOverpaymentTimeSaved] = useState<number>(0);
    const [overpaymentTotalSaved, setOverpaymentTotalSaved] = useState<number>(0);
    const [twoMortgageSaved, setTwoMortgageSaved] = useState<number>(0);
    const [cheaperMortgage, setCheaperMortgage] = useState<string>("Mortgage 1");
    const [twoMortgageInterestSaved, setTwoMortgageInterestSaved] = useState<number>(0);
    const [cheaperMortgageInterest, setCheaperMortgageInterest] = useState<string>("Mortgage 1");

    useEffect(() => { // Comparison calculations
        setOffsetSavingsInterest(offsetData.totalInterest - mortgageData.totalInterest);
        setOffsetTimeSaved(offsetData.interestAddedMonthly.length - mortgageData.interestAddedMonthly.length);
        setOverpaymentSavingsInterest(overpaymentData.totalInterest - mortgageData.totalInterest);
        setOverpaymentTimeSaved(overpaymentData.interestAddedMonthly.length - mortgageData.interestAddedMonthly.length);
        setOverpaymentTotalSaved(overpaymentData.totalAmountPaid - mortgageData.totalAmountPaid);
        if (mortgageData.totalAmountPaid > secondMortgageData.totalAmountPaid) {
            setTwoMortgageSaved(mortgageData.totalAmountPaid - secondMortgageData.totalAmountPaid);
            setCheaperMortgage("Mortgage 2");
        } else {
            setTwoMortgageSaved(secondMortgageData.totalAmountPaid - mortgageData.totalAmountPaid);
            setCheaperMortgage("Mortgage 1");
        }
        if (mortgageData.totalInterest > secondMortgageData.totalInterest) {
            setTwoMortgageInterestSaved(mortgageData.totalInterest - secondMortgageData.totalInterest);
            setCheaperMortgageInterest("Mortgage 2");
        } else {
            setTwoMortgageInterestSaved(secondMortgageData.totalInterest - mortgageData.totalInterest);
            setCheaperMortgageInterest("Mortgage 1");
        }

    }, [showOffset, showOverpayment, showTwoMortgages, mortgageData, secondMortgageData, offsetData, overpaymentData]);
    return (
        <div className="pt-3 px-3">
            <div className="flex flex-col md:flex-row justify-between items-stretch">
                <div className="w-full flex justify-end md:justify-end  space-x-4">
                    {showTwoMortgages ? (<MortgageComparison saving={twoMortgageSaved}
                        cheaperMortgage={cheaperMortgage}
                        interestSaved={twoMortgageInterestSaved}
                        cheaperInterest={cheaperMortgageInterest} />) : (<></>)}
                    {!showTwoMortgages && showOffset ? (<OffsetComparison saving={offsetSavingsInterest} timeSaved={offsetTimeSaved} />) : (<></>)}
                    {!showTwoMortgages && showOverpayment ? (<OverpaymentComparison savingInterest={overpaymentSavingsInterest}
                        timeSaved={overpaymentTimeSaved}
                        totalSaved={overpaymentTotalSaved} />) : (<></>)}
                </div>
            </div>
        </div>
    );
}

export default Comparisons;